import type { ICategory,IERPUser,IFacility,ISalesChannel,IVendor } from '@admin-panel/types';
import { formatToReadableDate } from '@admin-panel/utils';
import { useMany } from '@refinedev/core';
import { Descriptions,Flex,Tag,Typography } from 'antd';
import React from 'react';

type Props = {
  user?: IERPUser;
};

const renderValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
};

const renderNamesById = (
  ids: number[] | undefined,
  items: Array<{ id: number; name: string }> | undefined,
  isLoading: boolean,
) => {
  if (!ids || ids.length === 0) return '-';
  if (isLoading && !items) return 'Loading...';
  const names = ids
    .map(id => items?.find(item => item.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  return names.length ? names.join(', ') : 'Unknown';
};

const AccountSettings: React.FC<Props> = ({ user }) => {
  const vendorIds = user?.adminUserAttributes?.vendorIds ?? [];
  const vendorLookupIds = Array.from(
    new Set([user?.vendorId, ...vendorIds].filter((id): id is number => typeof id === 'number')),
  );
  const categoryIds = user?.adminUserAttributes?.categoryIds ?? [];
  const warehouseIds = user?.adminUserAttributes?.warehouseIds ?? [];
  const salesChannelIds = user?.adminUserAttributes?.salesChannelIds ?? [];
  const salesChannelLookupIds = Array.from(
    new Set(
      [...(user?.storeSalesChannelIds ?? []), ...salesChannelIds].filter(
        (id): id is number => typeof id === 'number',
      ),
    ),
  );

  const {
    result: vendors,
    query: { isLoading: vendorsLoading },
  } = useMany<IVendor>({
    resource: 'vendors',
    ids: vendorLookupIds,
    queryOptions: {
      enabled: vendorLookupIds.length > 0,
    },
  });

  const {
    result: categories,
    query: { isLoading: categoriesLoading },
  } = useMany<ICategory>({
    resource: 'categories',
    ids: categoryIds,
    queryOptions: {
      enabled: categoryIds.length > 0,
    },
  });

  const {
    result: facilities,
    query: { isLoading: facilitiesLoading },
  } = useMany<IFacility>({
    resource: 'facilities',
    ids: warehouseIds,
    queryOptions: {
      enabled: warehouseIds.length > 0,
    },
  });

  const {
    result: salesChannels,
    query: { isLoading: salesChannelsLoading },
  } = useMany<ISalesChannel>({
    resource: 'saleschannels',
    ids: salesChannelLookupIds,
    queryOptions: {
      enabled: salesChannelLookupIds.length > 0,
    },
  });

  if (!user) {
    return <Typography.Text type="secondary">User details are unavailable.</Typography.Text>;
  }

  const roleTags = user.roles?.length ? (
    <Flex gap={4} wrap>
      {user.roles.map(role => (
        <Tag bordered={false} key={role.id || role.name} color="geekblue" style={{ margin: 0 }}>
          {role.name}
        </Tag>
      ))}
    </Flex>
  ) : (
    '-'
  );

  const vendorName =
    user.vendorId !== undefined
      ? renderNamesById([user.vendorId], vendors?.data, vendorsLoading)
      : '-';

  return (
    <Descriptions column={1} size="small" labelStyle={{ width: 180 }}>
      <Descriptions.Item label="Name">{renderValue(user.name)}</Descriptions.Item>
      <Descriptions.Item label="Email">{renderValue(user.email)}</Descriptions.Item>
      <Descriptions.Item label="Roles">{roleTags}</Descriptions.Item>
      <Descriptions.Item label="Vendor ID">{vendorName}</Descriptions.Item>
      <Descriptions.Item label="Warehouse IDs">
        {renderNamesById(warehouseIds, facilities?.data, facilitiesLoading)}
      </Descriptions.Item>
      <Descriptions.Item label="Vendor IDs">
        {renderNamesById(vendorIds, vendors?.data, vendorsLoading)}
      </Descriptions.Item>
      <Descriptions.Item label="Category IDs">
        {renderNamesById(categoryIds, categories?.data, categoriesLoading)}
      </Descriptions.Item>
      <Descriptions.Item label="Store Sales Channel">
        {user.storeSalesChannelIds !== undefined
          ? renderNamesById(user.storeSalesChannelIds, salesChannels?.data, salesChannelsLoading)
          : '-'}
      </Descriptions.Item>
      <Descriptions.Item label="Sales Channel IDs">
        {renderNamesById(salesChannelIds, salesChannels?.data, salesChannelsLoading)}
      </Descriptions.Item>
      <Descriptions.Item label="Created On">
        {formatToReadableDate(user.createdOn)}
      </Descriptions.Item>
      <Descriptions.Item label="Last Login">
        {formatToReadableDate(user.lastLoginTime)}
      </Descriptions.Item>
    </Descriptions>
  );
};

export default AccountSettings;
